import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMG, waLink, WA_MSG } from "../../data/content";
import { EASE, MaskLines, Btn } from "../shared";
import { WhatsAppIcon } from "../Navbar";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-emerald-deep" data-testid="home-hero">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <motion.img
          src={IMG.hero}
          alt="Red lehenga bride and cream sherwani groom — The Big Wedd"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.8, ease: EASE }}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-deep/70 via-ink/25 to-emerald-deep/85" />
      </motion.div>

      <motion.div
        initial={{ y: 0 }}
        animate={{ y: "-100%" }}
        transition={{ duration: 1.1, delay: 0.3, ease: EASE }}
        className="absolute inset-0 z-30 bg-emerald-deep"
      />

      <div className="relative z-20 flex h-full flex-col items-center justify-end px-6 pb-28 text-center md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
          className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-gold-light"
        >
          Luxury Wedding &amp; Event Planners — Jaipur · Udaipur · Goa · Rishikesh
        </motion.p>

        <MaskLines
          onLoad
          delay={1.15}
          lines={[
            "Creating celebrations",
            "that become your",
            <span key="l3" className="italic text-gold-light">favourite memories.</span>,
          ]}
          lineClassName="font-serif text-ivory leading-[1.04] text-[clamp(2.6rem,7vw,5.8rem)]"
          className="mt-6 block"
        />

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8, duration: 0.9, ease: EASE }}
          className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
        >
          <Btn to="/enquire" testid="hero-cta-plan-my-wedding">Plan My Wedding</Btn>
          <Btn to="/gallery" variant="outline" className="border-ivory/60 text-ivory hover:bg-ivory hover:text-emerald" testid="hero-cta-explore-work">Explore Our Work</Btn>
          <a
            href={waLink(WA_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="inline-flex items-center gap-3 rounded-full px-6 py-4 text-[11px] md:text-xs font-medium uppercase tracking-[0.22em] text-ivory/90 transition-all duration-500 hover:text-gold-light"
          >
            <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2"
      >
        <div className="h-12 w-px overflow-hidden bg-ivory/20">
          <motion.div className="h-6 w-px bg-gold" animate={{ y: [0, 24, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }} />
        </div>
      </motion.div>
    </section>
  );
}
